import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import {
  useNotifications,
  useUnreadNotificationsCount,
  UserNotification,
  useAddNotificationToCache,
} from "../hooks/useNotifications";
import { notificationSound } from "../utils/notificationSound";
import { message } from "antd";
import { simpleNotificationService } from "../services/notificationService";

interface NotificationContextType {
  notifications: UserNotification[];
  unreadCount: number;
  isLoading: boolean;
  soundEnabled: boolean;
  isConnected: boolean;
  setSoundEnabled: (enabled: boolean) => void;
  refreshNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotificationContext must be used within a NotificationProvider"
    );
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  // Fetch notifications once on mount
  const { data: notifications = [], isLoading, refetch } = useNotifications();
  const { data: unreadCount = 0, refetch: refetchCount } =
    useUnreadNotificationsCount();

  // Hook to add notifications to cache
  const addNotificationToCache = useAddNotificationToCache();

  const [soundEnabled, setSoundEnabledState] = useState(() => {
    const stored = localStorage.getItem("notificationSoundEnabled");
    return stored ? stored === "true" : true;
  });

  const [isConnected, setIsConnected] = useState(false);

  // Use ref to prevent multiple setups
  const isSetup = useRef(false);

  // Initialize sound settings (only once)
  useEffect(() => {
    notificationSound.setEnabled(soundEnabled);
    const storedVolume = localStorage.getItem("notificationSoundVolume");
    if (storedVolume) {
      notificationSound.setVolume(parseFloat(storedVolume));
    }
  }, [soundEnabled]);

  // Handle new notifications
  const handleNewNotifications = useCallback(
    (newNotifications: any[]) => {
      console.log(`🔔 Processing ${newNotifications.length} new notifications`);

      newNotifications.forEach((notification) => {
        try {
          // Create notification object that matches our interface
          const newNotification: UserNotification = {
            id: notification.id,
            data: {
              message:
                notification.data?.message ||
                notification.message ||
                "New notification",
            },
            read_at: notification.read_at,
            created_at: notification.created_at,
            updated_at: notification.updated_at,
          };

          // Only process if it's actually unread
          if (!notification.read_at) {
            // Add to cache for real-time update
            addNotificationToCache(newNotification);

            // Play sound if enabled
            if (soundEnabled) {
              console.log(
                "🔊 Playing notification sound for:",
                newNotification.data.message
              );
              notificationSound.playNotificationSound().catch((error) => {
                console.error("❌ Failed to play notification sound:", error);
              });
            }

            // Show browser notification
            notificationSound
              .showBrowserNotification(
                "New Notification",
                newNotification.data.message,
                "/favicon.ico"
              )
              .catch((error) => {
                console.error("❌ Failed to show browser notification:", error);
              });

            // Show in-app message
            message.info({
              content: newNotification.data.message,
              duration: 4,
              style: { marginTop: "20vh" },
            });
          }
        } catch (error) {
          console.error("❌ Error processing notification:", error);
        }
      });

      // Refresh counts after processing
      refetchCount();
    },
    [soundEnabled, addNotificationToCache, refetchCount]
  );

  // Set up notification checking (only once)
  useEffect(() => {
    if (isSetup.current) return;

    console.log("🔌 Setting up simple notification service...");
    isSetup.current = true;

    // Mark existing notifications as seen to prevent duplicate processing
    if (notifications.length > 0) {
      const existingIds = notifications.map((n) => n.id);
      simpleNotificationService.markAsSeen(existingIds);
      console.log(
        `✅ Marked ${existingIds.length} existing notifications as seen`
      );
    }

    // Add listener for new notifications
    const removeListener = simpleNotificationService.addListener(
      handleNewNotifications
    );

    // Start checking for new notifications
    simpleNotificationService.startChecking();

    // Monitor service status
    const statusInterval = setInterval(() => {
      const status = simpleNotificationService.getStatus();
      setIsConnected(status.isPolling);
    }, 5000);

    return () => {
      console.log("🧹 Cleaning up simple notification service...");
      removeListener();
      simpleNotificationService.cleanup();
      clearInterval(statusInterval);
      isSetup.current = false;
    };
  }, [notifications, handleNewNotifications]); // Include notifications to mark existing ones as seen

  // Request notification permission on mount
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().then((permission) => {
        console.log("🔔 Notification permission:", permission);
      });
    }
  }, []);

  // Function to toggle sound
  const setSoundEnabled = useCallback((enabled: boolean) => {
    console.log("🔊 Setting sound enabled:", enabled);
    setSoundEnabledState(enabled);
    notificationSound.setEnabled(enabled);
    localStorage.setItem("notificationSoundEnabled", enabled.toString());
  }, []);

  // Function to refresh notifications manually
  const refreshNotifications = useCallback(async () => {
    console.log("🔄 Manually refreshing notifications...");
    try {
      await Promise.all([refetch(), refetchCount()]);
      await simpleNotificationService.checkNow();
      message.success("Notifications refreshed");
    } catch (error) {
      console.error("❌ Failed to refresh notifications:", error);
      message.error("Failed to refresh notifications");
    }
  }, [refetch, refetchCount]);

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    isLoading,
    soundEnabled,
    isConnected,
    setSoundEnabled,
    refreshNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
